import { DraggableAttributes } from "@dnd-kit/core";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faGripDotsVertical } from "@fortawesome/pro-solid-svg-icons";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { AnimatePresence } from "framer-motion";
import React, { ReactNode, useRef, useState, useCallback } from "react";
import { twJoin, twMerge } from "tailwind-merge";
import { useOnClickOutside } from "usehooks-ts";

import { LOCK_NODE_EDITOR_CLASSNAME } from "src/authoringMultiplayerLock/constants";
import { Icon } from "src/base-components/Icon";
import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import { DropdownItem } from "src/design-system/Dropdown";

type MenuItemProps = {
  children: ReactNode;
  onClick: () => void;
  icon: IconProp;
};

const ContextMenuItem: React.FC<MenuItemProps> = ({
  children,
  icon,
  onClick,
}) => {
  return (
    <MenuItem as={DropdownItem} onClick={onClick}>
      <div className="flex items-center gap-2">
        <Icon color="text-gray-500" icon={icon} size="2xs" />
        <div className="shrink-0">{children}</div>
      </div>
    </MenuItem>
  );
};

type SubComponents = {
  Item: typeof ContextMenuItem;
};

export type ContextMenuProps = {
  readonly?: boolean;
  children: ReactNode;
  button: "vertical" | "horizontal";
  buttonProps?: Partial<SyntheticListenerMap & DraggableAttributes>;
  show: boolean;
  hide?: boolean;
};

export const ContextMenu: React.FC<ContextMenuProps> & SubComponents = ({
  children,
  button,
  buttonProps,
  show,
  hide,
}) => {
  const [isOpen, setOpen] = useState(false);
  const pointerPosition = useRef({ x: 0, y: 0 });
  const ref = useRef<HTMLDivElement>(null);
  const {
    onPointerUp: onPointerUpButtonProps,
    onPointerDown: onPointerDownButtonProps,
  } = buttonProps || {
    onPointerUp: null,
    onPointerDown: null,
  };

  useOnClickOutside(ref, () => {
    setOpen(false);
  });

  const handlePointerDown: React.PointerEventHandler<HTMLButtonElement> =
    useCallback(
      (e) => {
        onPointerDownButtonProps?.(e);
        setOpen(false);

        pointerPosition.current = {
          x: Math.trunc(e.pageX),
          y: Math.trunc(e.pageY),
        };
      },
      [onPointerDownButtonProps],
    );

  const handlePointerUp: React.PointerEventHandler<HTMLButtonElement> =
    useCallback(
      (e) => {
        onPointerUpButtonProps?.(e);

        const [clickX, clickY] = [Math.trunc(e.pageX), Math.trunc(e.pageY)];
        if (
          clickX !== pointerPosition.current.x ||
          clickY !== pointerPosition.current.y
        ) {
          return;
        }

        setOpen(true);
      },
      [pointerPosition, setOpen, onPointerUpButtonProps],
    );

  return (
    <Menu ref={ref} as="div">
      <MenuButton
        {...buttonProps}
        className={twMerge(
          "relative z-50 m-auto flex rounded border border-gray-300 bg-white opacity-0 shadow-sm transition-opacity duration-300 hover:opacity-100",
          "outline-none focus:outline-none",
          button === "horizontal" && "px-1 py-0.5",
          button === "vertical" && "px-0.5 py-1",
          (show || isOpen) && "opacity-100",
          hide && "opacity-0",
        )}
        disabled={isOpen}
        tabIndex={-1}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
      >
        <div className="flex items-center">
          <Icon
            color={twJoin(
              "p-0 text-2xs text-gray-500",
              button === "horizontal" && "rotate-90",
            )}
            icon={faGripDotsVertical}
            size="2xs"
          />
        </div>
      </MenuButton>
      <AnimatePresence initial={false}>
        {isOpen && (
          <MenuItems
            anchor={{
              to: button === "vertical" ? "bottom start" : "bottom",
              offset: 4,
            }}
            as={MotionDropdownPanel}
            className={twJoin("w-max flex-col", LOCK_NODE_EDITOR_CLASSNAME)}
            static
          >
            {children}
          </MenuItems>
        )}
      </AnimatePresence>
    </Menu>
  );
};

ContextMenu.Item = ContextMenuItem;
